import CurrencyInput from "../components/CurrencyInput";
import PercentageInput from "../components/PercentageInput";
import { MarketStatistic } from "../components/MarketStatistic";
import { SectionHeader } from "../components/SectionHeader";
import { ShowMoreToggle } from "../components/ShowMoreToggle";
import { useLiabilitiesLogic } from "./Liabilities.logic";
import { useFieldStatistic } from "../hooks/useStatistics";
import { useStatisticsValue } from "../hooks/useStatisticsValue";

/**
 * LiabilitiesForm - Pure form component (no layout)
 * Used by both LiabilitiesSettings.mobile (with SettingsPageLayout) and SettingsModal
 *
 * Essential fields: mortgage+rate, creditCard+rate, carLoan+rate, studentLoans+rate,
 *   lineOfCredit+rate, otherLiabilities+rate
 * Advanced fields: investmentPropertyMortgage+rate, heloc+rate, businessLoans+rate,
 *   taxesPayable, mortgageAmortizationRemaining
 */
export function LiabilitiesForm() {
  const {
    formData,
    handleChange,
    handleBlur,
    loading,
    totalLiabilities,
    showAdvanced,
    toggleAdvanced,
  } = useLiabilitiesLogic();

  // Get age-specific statistic value
  const liabilitiesStatistic = useStatisticsValue("liabilities");

  // Get statistics help text for each field
  const mortgageHelp = useFieldStatistic(
    "mortgage",
    "Outstanding mortgage balance"
  );
  const mortgageInterestRateHelp = useFieldStatistic(
    "mortgageInterestRate",
    "Annual interest rate for your mortgage"
  );
  const creditCardBalanceHelp = useFieldStatistic(
    "creditCardBalance",
    "Outstanding credit card balance"
  );
  const creditCardInterestRateHelp = useFieldStatistic(
    "creditCardInterestRate",
    "Annual interest rate for your credit card"
  );
  const carLoanHelp = useFieldStatistic(
    "carLoan",
    "Outstanding balance remaining on vehicle loan"
  );
  const carLoanInterestRateHelp = useFieldStatistic(
    "carLoanInterestRate",
    "Annual interest rate for your car loan"
  );
  const lineOfCreditHelp = useFieldStatistic(
    "lineOfCredit",
    "Personal or home equity line of credit balance"
  );
  const lineOfCreditInterestRateHelp = useFieldStatistic(
    "lineOfCreditInterestRate",
    "Annual interest rate for your line of credit"
  );
  const studentLoansHelp = useFieldStatistic(
    "studentLoans",
    "Federal and provincial student loan balances"
  );
  const studentLoanInterestRateHelp = useFieldStatistic(
    "studentLoanInterestRate",
    "Annual interest rate for your student loans"
  );
  const otherLiabilitiesHelp = useFieldStatistic(
    "otherLiabilities",
    "Any other debts not listed above"
  );
  const otherLiabilitiesInterestRateHelp = useFieldStatistic(
    "otherLiabilitiesInterestRate",
    "Annual interest rate for your other liabilities"
  );

  // Advanced field statistics
  const investmentPropertyMortgageHelp = useFieldStatistic(
    "investmentPropertyMortgage",
    "Mortgage on rental/investment property"
  );
  const investmentPropertyMortgageRateHelp = useFieldStatistic(
    "investmentPropertyMortgageRate",
    "Annual rate for investment property mortgage"
  );
  const helocHelp = useFieldStatistic(
    "heloc",
    "Home equity line of credit balance"
  );
  const helocInterestRateHelp = useFieldStatistic(
    "helocInterestRate",
    "Annual rate for HELOC"
  );
  const businessLoansHelp = useFieldStatistic(
    "businessLoans",
    "Business loans and commercial debt"
  );
  const businessLoansInterestRateHelp = useFieldStatistic(
    "businessLoansInterestRate",
    "Annual rate for business loans"
  );
  const taxesPayableHelp = useFieldStatistic(
    "taxesPayable",
    "CRA payment arrangements and tax arrears"
  );

  if (loading) {
    return null;
  }

  return (
    <div className="settings-form-container" id="liabilities-form">
      <form className="settings-form-scroll">
        {/* ── Real Estate Debt ── */}
        <SectionHeader title="Real Estate Debt" sectionId="real-estate-debt" />

        <div className="form-grid">
          <CurrencyInput
            name="mortgage"
            value={formData.mortgage}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Mortgage"
            helpText={mortgageHelp}
          />

          <PercentageInput
            name="mortgageInterestRate"
            value={formData.mortgageInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Mortgage Interest Rate"
            min={0}
            max={30}
            helpText={mortgageInterestRateHelp}
          />
        </div>

        {/* ── Consumer Debt ── */}
        <SectionHeader title="Consumer Debt" sectionId="consumer-debt" />

        <div className="form-grid">
          <CurrencyInput
            name="creditCardBalance"
            value={formData.creditCardBalance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Credit Card Balance"
            helpText={creditCardBalanceHelp}
          />

          <PercentageInput
            name="creditCardInterestRate"
            value={formData.creditCardInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Credit Card Interest Rate"
            min={0}
            max={40}
            helpText={creditCardInterestRateHelp}
          />

          <CurrencyInput
            name="carLoan"
            value={formData.carLoan}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Car Loan"
            helpText={carLoanHelp}
          />

          <PercentageInput
            name="carLoanInterestRate"
            value={formData.carLoanInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Car Loan Interest Rate"
            min={0}
            max={30}
            helpText={carLoanInterestRateHelp}
          />

          <CurrencyInput
            name="lineOfCredit"
            value={formData.lineOfCredit}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Line of Credit"
            helpText={lineOfCreditHelp}
          />

          <PercentageInput
            name="lineOfCreditInterestRate"
            value={formData.lineOfCreditInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Line of Credit Interest Rate"
            min={0}
            max={30}
            helpText={lineOfCreditInterestRateHelp}
          />
        </div>

        {/* ── Education Debt ── */}
        <SectionHeader title="Education Debt" sectionId="education-debt" />

        <div className="form-grid">
          <CurrencyInput
            name="studentLoans"
            value={formData.studentLoans}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Student Loans"
            helpText={studentLoansHelp}
          />

          <PercentageInput
            name="studentLoanInterestRate"
            value={formData.studentLoanInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Student Loan Interest Rate"
            min={0}
            max={20}
            helpText={studentLoanInterestRateHelp}
          />
        </div>

        {/* ── Other Debt ── */}
        <SectionHeader title="Other Debt" sectionId="other-debt" />

        <div className="form-grid">
          <CurrencyInput
            name="otherLiabilities"
            value={formData.otherLiabilities}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Other Liabilities"
            helpText={otherLiabilitiesHelp}
          />

          <PercentageInput
            name="otherLiabilitiesInterestRate"
            value={formData.otherLiabilitiesInterestRate}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Other Liabilities Interest Rate"
            min={0}
            max={40}
            helpText={otherLiabilitiesInterestRateHelp}
          />
        </div>

        {/* ── Advanced Fields ── */}
        <ShowMoreToggle expanded={showAdvanced} onToggle={toggleAdvanced}>
          <SectionHeader
            title="Investment Property Debt"
            sectionId="investment-property-debt"
          />

          <div className="form-grid">
            <CurrencyInput
              name="investmentPropertyMortgage"
              value={formData.investmentPropertyMortgage}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Investment Property Mortgage"
              helpText={investmentPropertyMortgageHelp}
            />

            <PercentageInput
              name="investmentPropertyMortgageRate"
              value={formData.investmentPropertyMortgageRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Investment Property Mortgage Rate"
              min={0}
              max={30}
              helpText={investmentPropertyMortgageRateHelp}
            />

            <CurrencyInput
              name="heloc"
              value={formData.heloc}
              onChange={handleChange}
              onBlur={handleBlur}
              label="HELOC"
              helpText={helocHelp}
            />

            <PercentageInput
              name="helocInterestRate"
              value={formData.helocInterestRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="HELOC Interest Rate"
              min={0}
              max={30}
              helpText={helocInterestRateHelp}
            />
          </div>

          <SectionHeader
            title="Business & Tax Debt"
            sectionId="business-tax-debt"
          />

          <div className="form-grid">
            <CurrencyInput
              name="businessLoans"
              value={formData.businessLoans}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Business Loans"
              helpText={businessLoansHelp}
            />

            <PercentageInput
              name="businessLoansInterestRate"
              value={formData.businessLoansInterestRate}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Business Loans Interest Rate"
              min={0}
              max={30}
              helpText={businessLoansInterestRateHelp}
            />

            <CurrencyInput
              name="taxesPayable"
              value={formData.taxesPayable}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Taxes Payable"
              helpText={taxesPayableHelp}
            />
          </div>

          <SectionHeader
            title="Mortgage Details"
            sectionId="mortgage-details"
          />

          <div className="form-grid">
            <CurrencyInput
              name="mortgageAmortizationRemaining"
              value={formData.mortgageAmortizationRemaining}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Mortgage Amortization Remaining (years)"
              helpText="Optional: remaining years on your mortgage"
              placeholder="25"
            />
          </div>
        </ShowMoreToggle>
      </form>

      {/* Total (calculated, read-only) - Sticky at bottom for long forms */}
      <div className="form-sticky-save" id="liabilities-total-bar">
        {/* Market Statistic Information - Between Total label and horizontal rule */}
        <div className="form-total-statistic-spacing">
          <MarketStatistic
            label="Canadian Liabilities Statistic"
            marketAverage={liabilitiesStatistic}
            currency={true}
          />
        </div>

        <div className="form-page-total-row">
          <span className="form-page-total-label">Total Liabilities</span>
          <span id="total" className="form-page-total-value-red">
            ${totalLiabilities.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
